import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { useAuth } from '../AuthContext';
import './styles/Product.css';

const Product = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const apiUrl = process.env.REACT_APP_API_URL;
  const [product, setProduct] = useState(null);
  const [selectedImage, setSelectedImage] = useState('');
  const [loading, setLoading] = useState(true);
  const [contribution, setContribution] = useState('');
  const [error, setError] = useState('');
  const { user } = useAuth();

  useEffect(() => {
    const fetchProduct = async () => {
      try {
        const response = await fetch(`${apiUrl}/products/${id}`);
        if (!response.ok) {
          throw new Error("Produit non trouvé.");
        }
        const data = await response.json();
        setProduct(data);
        setSelectedImage(data.images && data.images.length > 0 ? data.images[0] : '');
      } catch (error) {
        console.error("Erreur lors du chargement du produit :", error);
      } finally {
        setLoading(false);
      }
    };

    fetchProduct();
  }, [id, apiUrl]);

  const handleContribution = () => {
    setError('');

    if (!user) {
      navigate('/login');
      return;
    }

    const contributionAmount = parseFloat(contribution);

    if (!contribution || contributionAmount <= 0) {
      setError("Veuillez entrer un montant valide.");
      return;
    }

    if (contributionAmount > product.price - product.collected) {
      setError("Montant trop élevé, veuillez ajuster.");
      return;
    }

    navigate('/payment', {
      state: {
        productId: product.id,
        productName: product.name,
        amount: contribution,
      },
    });
  };

  if (loading) return <div className="product-loading">Chargement du produit...</div>;
  if (!product) return <div className="product-error">Produit introuvable</div>;

  return (
    <main className="product">
      <Helmet>
        <title>{product.name} - Gagnez {product.name} grâce à votre contribution sur WinlyDeal</title>
        <meta 
          name="description" 
          content={`Tentez de remporter ${product.name} en contribuant sur WinlyDeal. Plus vous contribuez, plus vous avez de chances de gagner !`} 
        />
        <meta 
          name="keywords" 
          content={`WinlyDeal, ${product.name}, gagner ${product.name}, contribuer, produits à gagner, deals, plateforme de contribution`} 
        />
        <link rel="canonical" href={`https://winlydeal.com/product/${product.id}`} />
        <meta name="robots" content="index, follow" />

        <meta property="og:title" content={`${product.name} - Participez et remportez | WinlyDeal`} />
        <meta 
          property="og:description" 
          content={`Découvrez ${product.name} sur WinlyDeal : contribuez et devenez le gagnant ! Inscrivez-vous et participez dès maintenant.`} 
        />
        <meta property="og:type" content="product" />
        <meta property="og:url" content={`https://winlydeal.com/product/${product.id}`} />
        <meta property="og:image" content={`https://winlydeal.com/images/${selectedImage}`} />

        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content={`${product.name} - Gagnez sur WinlyDeal`} />
        <meta 
          name="twitter:description" 
          content={`Contribuez pour remporter ${product.name} grâce à WinlyDeal. Rejoignez notre communauté et tentez votre chance !`} 
        />
        <meta name="twitter:image" content={`https://winlydeal.com/images/${selectedImage}`} />

        <script type="application/ld+json">
          {`
            {
              "@context": "https://schema.org/",
              "@type": "Product",
              "name": "${product.name}",
              "image": "https://winlydeal.com/images/${selectedImage}",
              "description": "${product.description}",
              "brand": {
                "@type": "Brand",
                "name": "WinlyDeal"
              },
              "offers": {
                "@type": "Offer",
                "url": "https://winlydeal.com/product/${product.id}",
                "priceCurrency": "EUR",
                "price": "${product.price}",
                "availability": "https://schema.org/InStock"
              }
            }
          `}
        </script>
      </Helmet>

      <section className="product-header">
        <div className="product-images-container">
          {product.images && product.images.length > 0 && (
            <>
              <div className="product-images-thumbnails">
                {product.images.map((image, index) => (
                  <div
                    key={index}
                    className={`product-images-thumbnail ${selectedImage === image ? 'active' : ''}`}
                    onClick={() => setSelectedImage(image)}
                  >
                    <img src={`/images/${image}`} alt={`Aperçu ${index + 1} de ${product.name}`} />
                  </div>
                ))}
              </div>
              <div className="product-images-main-image">
                <img src={`/images/${selectedImage}`} alt={`Visuel principale de ${product.name}`} />
              </div>
            </>
          )}
        </div>

        <div className="product-info">
          <h1 className="product-title">{product.name}</h1>
          <p className="product-description">{product.description}</p>
        </div>
      </section>

      <section className="product-contribution">
        <h2 className="product-contribution-title">Contribuez pour remporter {product.name}</h2>
        <p className="product-contribution-text">
          Collecté : {product.collected} € sur {product.price} €
        </p>
        <div className="product-contribution-bar">
          <div
            className={`product-contribution-progress ${
              (product.collected / product.price) < 0.5
                ? 'low'
                : (product.collected / product.price) < 0.8
                ? 'medium'
                : 'high'
            }`}
            style={{ width: `${(product.collected / product.price) * 100}%` }}
          ></div>
        </div>

        <input
          type="number"
          className="product-contribution-input"
          placeholder="Entrez un montant de contribution"
          value={contribution}
          onChange={(e) => setContribution(e.target.value)}
        />
        {error && <p className="product-error-message">{error}</p>}
        <button className="product-contribution-button" onClick={handleContribution}>
          Contribuer maintenant
        </button>
      </section>

      <section className="product-details">
        <h2 className="product-details-title">Détails du produit</h2>
        <ul className="product-details-list">
          <li>Prix fixe : {product.price} €</li>
          <li>Participants actuels : {product.participants}</li>
          <li>Suivez l'avancée de la collecte en temps réel !</li>
        </ul>
      </section>
    </main>
  );
};

export default Product;
